/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { isSameMonth, type DateValue } from '@internationalized/date';

import { getMonthKey } from './CalendarGrid';
import type { CalendarLayout } from './date-picker-types';

export function getScrollParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

/**
 * Stacked layout only: on mount, scrolls the calendar's nearest scrolling
 * ancestor so the month holding `initialScrollDate` sits just below the sticky
 * weekday row. Does nothing when that date is in the first visible month.
 *
 * Runs once, before the first paint, so the calendar opens already scrolled
 * instead of visibly jumping. Later date changes deliberately don't scroll.
 */
export function useStackedInitialScroll(
    calendarRef: React.RefObject<HTMLElement>,
    layout: CalendarLayout,
    initialScrollDate: DateValue | null | undefined,
    visibleStart: DateValue
) {
    React.useLayoutEffect(() => {
        const calendar = calendarRef.current;
        if (
            layout !== 'stacked' ||
            !calendar ||
            !initialScrollDate ||
            isSameMonth(initialScrollDate, visibleStart)
        ) {
            return;
        }

        const grid = calendar.querySelector(
            `[data-month="${getMonthKey(initialScrollDate)}"]`
        );
        const weekDays = calendar.querySelector('.calendar-weekdays');
        const scroller = getScrollParent(calendar);
        if (!grid || !weekDays || !scroller) {
            return;
        }

        // Leave the same gap below the stuck weekday row as the first month has.
        const weekDaysHeight =
            weekDays.getBoundingClientRect().height +
            (parseFloat(getComputedStyle(weekDays).marginBottom) || 0);

        scroller.scrollTop +=
            grid.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top -
            weekDaysHeight;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

/**
 * Snaps `scrollRef` back to the top when `date` goes from set to cleared, such
 * as on Reset, then calls `onCleared`. Runs before paint so the old position
 * never shows. Does nothing while the date stays set or stays empty.
 */
export function useScrollTopOnClear(
    scrollRef: React.RefObject<HTMLElement>,
    date: DateValue | null,
    onCleared: () => void
) {
    const previousDate = React.useRef(date);

    React.useLayoutEffect(() => {
        const wasCleared = previousDate.current !== null && date === null;
        previousDate.current = date;

        if (!wasCleared) {
            return;
        }

        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
        onCleared();

        // Only a change to the date should snap; onCleared is usually inline.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);
}
